import React, { useState } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { useConfig } from '../../contexts/ConfigContext';
import { getSpecializationTerm, getSpecializationPrefix } from '../../utils/organizationHelpers';
import { useIframeCommunication } from '../../hooks/useIframeCommunication';
import GroupsList from '../../components/GroupsList';
import Footer from '../../components/Footer';
import './MainPage.css';

const MainPage = () => {
    // Общение с родительским окном через postMessage
    useIframeCommunication();
    const { config } = useConfig();
    const orgCode = config?.code || 'mag';
    const [selectedDirection, setSelectedDirection] = useState(null);
    const [selectedRegion, setSelectedRegion] = useState(null);
    const [isFilterRefreshing, setIsFilterRefreshing] = useState(false);
    const groupsContainerRef = React.useRef(null);

    // Загрузка направлений для фильтров
    const { data: directions } = useQuery('publicDirections', () =>
        axios.get(`${API_BASE_URL}/directions/public`).then(res => res.data)
    );

    // Загрузка групп
    const { data: allGroupsRaw, isLoading: groupsLoading } = useQuery(
        ['publicGroups'],
        () => axios.get(`${API_BASE_URL}/groups/public`).then(res => res.data),
        { keepPreviousData: true }
    );

    // Фильтруем группы: open/started_additional; с расписанием или с форматом "регулярно"
    const hasRegularFormat = (g) => (g.format_data?.[0]?.frequency === 'weekly' || g.format_data?.[0]?.frequency === 'biweekly');
    const allGroups = React.useMemo(() => {
        if (!allGroupsRaw) return [];
        return allGroupsRaw.filter(group => {
            const status = group.recruitment_status;
            if (status !== 'open' && status !== 'started_additional') return false;
            return group.earliest_event_date || hasRegularFormat(group);
        });
    }, [allGroupsRaw]);

    // Группируем направления - объединяем все "Специализация: *" / "Авторская программа: *" в одно
    // И показываем только те направления, для которых есть доступные группы
    const displayDirections = React.useMemo(() => {
        if (!directions || !allGroups) return [];

        const prefix = getSpecializationPrefix(orgCode);
        const specializationTerm = getSpecializationTerm(orgCode);

        // Получаем уникальные ID направлений, для которых есть группы
        const availableDirectionIds = new Set(allGroups.map(g => g.direction_id).filter(Boolean));

        // Фильтруем только доступные направления
        const availableDirections = directions.filter(d => availableDirectionIds.has(d.id));

        const specializations = availableDirections.filter(d => d.name.startsWith(prefix));
        const otherDirections = availableDirections.filter(d => !d.name.startsWith(prefix));

        const result = [];

        // Добавляем объединенную кнопку "Специализация" / "Авторская программа", если есть такие направления
        if (specializations.length > 0) {
            result.push({
                id: 'specialization',
                name: specializationTerm,
                directionIds: specializations.map(s => s.id)
            });
        }

        // Добавляем остальные направления
        otherDirections.forEach(d => {
            result.push({
                id: d.id,
                name: d.name,
                directionIds: [d.id]
            });
        });

        return result;
    }, [directions, allGroups, orgCode]);

    // Формируем список ID направлений для фильтрации
    const selectedDirectionIds = React.useMemo(() => {
        if (!selectedDirection) return null;
        const direction = displayDirections.find(d => d.id === selectedDirection);
        return direction ? direction.directionIds : null;
    }, [selectedDirection, displayDirections]);

    // Фильтрация групп на клиенте + сортировка по дате:
    // сначала ближайшие будущие/текущие, затем без даты, в самом конце — уже прошедшие
    const groups = React.useMemo(() => {
        if (!allGroups) return [];

        const filtered = allGroups.filter(group => {
            // Фильтр по направлению
            if (selectedDirectionIds && !selectedDirectionIds.includes(group.direction_id)) {
                return false;
            }

            // Фильтр по региону (Москва / Онлайн / Регионы)
            if (selectedRegion) {
                const regionName = group.region_name || '';
                const hasMoscow = regionName.includes('Москва');
                const hasOnline = regionName.includes('Онлайн');

                if (selectedRegion === 'Москва') {
                    // Группа попадает в "Москва", если в названии региона есть "Москва"
                    if (!hasMoscow) return false;
                } else if (selectedRegion === 'Онлайн') {
                    // Группа попадает в "Онлайн", если в названии региона есть "Онлайн"
                    if (!hasOnline) return false;
                } else if (selectedRegion === 'Регионы') {
                    // В "Регионы" попадают только те, где нет ни "Москва", ни "Онлайн"
                    if (hasMoscow || hasOnline) return false;
                }
            }

            return true;
        });

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const getKey = (g) => {
            if (!g.earliest_event_date) {
                // Группы без даты (например, регулярные без расписания) — после будущих, до прошедших
                return { bucket: 1, time: Number.MAX_SAFE_INTEGER };
            }
            const d = new Date(g.earliest_event_date);
            d.setHours(0, 0, 0, 0);
            const ts = d.getTime();
            if (ts >= today.getTime()) {
                // Будущие / сегодняшние — первыми, по возрастанию даты
                return { bucket: 0, time: ts };
            }
            // Уже прошедшие — в конец
            return { bucket: 2, time: ts };
        };

        return [...filtered].sort((a, b) => {
            const ka = getKey(a);
            const kb = getKey(b);
            if (ka.bucket !== kb.bucket) return ka.bucket - kb.bucket;
            // Внутри будущих сортируем по дате по возрастанию
            if (ka.bucket === 0) return ka.time - kb.time;
            // Внутри прошедших — по дате по убыванию (самые недавние выше)
            if (ka.bucket === 2) return kb.time - ka.time;
            // Для bucket 1 (без даты) — порядок не принципиален
            return 0;
        });
    }, [allGroups, selectedDirectionIds, selectedRegion]);

    const handleDirectionClick = (directionId) => {
        setSelectedDirection(prev => (prev === directionId ? null : directionId));
        setIsFilterRefreshing(true);
    };

    const handleRegionClick = (regionKey) => {
        setSelectedRegion(prev => (prev === regionKey ? null : regionKey));
        setIsFilterRefreshing(true);
    };

    // После применения фильтра скроллим к списку групп (учитывая, что страница может быть в iframe)
    React.useEffect(() => {
        if (!isFilterRefreshing) return;

        const timeout = setTimeout(() => {
            setIsFilterRefreshing(false);
            if (groupsContainerRef.current) {
                groupsContainerRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        }, 150);

        return () => clearTimeout(timeout);
    }, [isFilterRefreshing]);

    return (
        <div className="main-page">
            <div className="filter-buttons">
                <h4>Какая группа вас интересует?</h4>
                <br />
                <div className="button-row">
                    {displayDirections?.map(direction => (
                        <button
                            key={direction.id}
                            className={selectedDirection === direction.id ? 'active-filter' : ''}
                            onClick={() => handleDirectionClick(direction.id)}
                        >
                            {direction.name}
                        </button>
                    ))}
                </div>
                <br />

                <h4>Место проведения группы:</h4>
                <br />
                <div className="button-row">
                    <button
                        className={selectedRegion === 'Москва' ? 'active-filter' : ''}
                        onClick={() => handleRegionClick('Москва')}
                    >
                        Москва
                    </button>
                    <button
                        className={selectedRegion === 'Онлайн' ? 'active-filter' : ''}
                        onClick={() => handleRegionClick('Онлайн')}
                    >
                        Онлайн
                    </button>
                    <button
                        className={selectedRegion === 'Регионы' ? 'active-filter' : ''}
                        onClick={() => handleRegionClick('Регионы')}
                    >
                        Регионы
                    </button>
                </div>
                <br />
                <hr width="100%" />
                <br />
            </div>

            <div ref={groupsContainerRef}>
                <GroupsList
                    groups={groups}
                    loading={groupsLoading || isFilterRefreshing}
                    orgCode={orgCode}
                />
            </div>
            <Footer />
        </div>
    );
};

export default MainPage;

